import type { ICampaignBudgetSummary, IOtherInstrumentsInLoi } from '@brushfire/types'
import {
  BUDGET_WIZARD_TABS,
  MIN_REWARD_USD,
  MIN_RESPONSE_TARGET,
  type BudgetTabConfirmations,
  type BudgetWizardTab,
} from './campaignBudgetConstants'

/** Accepts current API (`totalBudget`) and legacy (`totalCost`) shapes from campaign-service. */
type OtherInstrumentsInLoiPayload = Partial<
  IOtherInstrumentsInLoi & { totalCost?: number }
>

type ResponseTargetingModeOption = 'standard' | 'scientific' | null

export function normalizeOtherInstrumentsInLoi(
  raw: OtherInstrumentsInLoiPayload | null | undefined,
): IOtherInstrumentsInLoi {
  const totalBudget = raw?.totalBudget ?? raw?.totalCost ?? 0
  const instrumentCount = raw?.instrumentCount ?? 0
  return {
    instrumentCount: Number.isFinite(instrumentCount) ? instrumentCount : 0,
    totalBudget: Number.isFinite(totalBudget) ? totalBudget : 0,
  }
}

export function normalizeCampaignBudgetSummary(
  raw: ICampaignBudgetSummary & {
    otherInstrumentsInLoi?: OtherInstrumentsInLoiPayload | null
  },
): ICampaignBudgetSummary {
  return {
    ...raw,
    otherInstrumentsInLoi: normalizeOtherInstrumentsInLoi(raw.otherInstrumentsInLoi),
  }
}

export function estimatedInstrumentCost(
  rewardPerResponse: number,
  responseTarget: number | null | undefined,
): number {
  if (responseTarget == null || responseTarget <= 0) return 0
  return rewardPerResponse * responseTarget
}

export function formatUsd(amount: number): string {
  const value = Number.isFinite(amount) ? amount : 0
  return value.toLocaleString(undefined, {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })
}

export function minRewardPerResponseMessage(): string {
  return `Minimum reward per response is ${formatUsd(MIN_REWARD_USD)}`
}

export function minRewardPerResponseHint(): string {
  return `Minimum ${formatUsd(MIN_REWARD_USD)} per response`
}

export function formatUsdCompact(amount: number): string {
  return formatUsd(amount)
}

export function formatRewardInputDisplay(amount: number): string {
  return amount.toFixed(2)
}

export function hasFixedResponseTarget(value: number | null | undefined): boolean {
  return value != null && Number.isFinite(value) && value > 0
}

export type ResponseTargetValidationOptions = {
  targetingMode?: ResponseTargetingModeOption
  optional?: boolean
  matchPending?: boolean
  /** BFP-1142 — live edit: responses already collected are the floor. */
  minCollected?: number
}

export function responseTargetBelowCollectedMessage(collected: number): string {
  return `Target response count cannot be lower than the number of responses already collected (${collected.toLocaleString()}).`
}

/** Slots still to fill — republish compares the matching pool to this, not the full target. */
export function remainingResponsesNeeded(responseTarget: number, collected: number): number {
  return Math.max(0, responseTarget - Math.max(0, collected))
}

export function validateResponseTarget(
  value: number | null | undefined,
  matchingSpCount: number,
  options?: ResponseTargetValidationOptions,
): string | null {
  const isScientific = options?.targetingMode === 'scientific'
  const optional = options?.optional === true
  if (value == null || !Number.isFinite(value) || value <= 0) {
    return optional ? null : 'Response target is required'
  }
  const minCollected = options?.minCollected ?? 0
  if (minCollected > 0 && value < minCollected) {
    return responseTargetBelowCollectedMessage(minCollected)
  }
  // Live edit: already-collected count replaces the 800 first-publish floor.
  if (!isScientific && !optional && minCollected === 0 && value < MIN_RESPONSE_TARGET) {
    return `Minimum response target is ${MIN_RESPONSE_TARGET}`
  }
  const remaining = remainingResponsesNeeded(value, minCollected)
  if (!optional && !options?.matchPending && remaining > matchingSpCount) {
    return `Response target cannot exceed your matching audience of ${matchingSpCount.toLocaleString()} Signal Providers`
  }
  return null
}

export function canConfirmResponseTargetTab(
  responseTarget: number | null | undefined,
  matchingSpCount: number,
  options?: ResponseTargetValidationOptions,
): boolean {
  return validateResponseTarget(responseTarget, matchingSpCount, options) === null
}

/** Allow digits and a single decimal separator while typing (max 2 fraction digits). */
export function sanitizeRewardInput(raw: string): string {
  let s = raw.replaceAll(/[^\d.]/g, '')
  const dot = s.indexOf('.')
  if (dot !== -1) {
    s = `${s.slice(0, dot + 1)}${s.slice(dot + 1).replaceAll('.', '')}`
  }
  const [whole, fraction] = s.split('.')
  if (fraction !== undefined) {
    return `${whole}.${fraction.slice(0, 2)}`
  }
  return whole
}

/** False while the user is mid-decimal (e.g. `"12."`). */
export function isCompleteRewardInput(raw: string): boolean {
  const t = raw.trim()
  if (t === '' || t === '.') return false
  return !t.endsWith('.')
}

export function parseRewardInput(raw: string): number | null {
  if (!isCompleteRewardInput(raw)) return null
  const n = Number.parseFloat(raw.trim())
  return Number.isFinite(n) ? n : null
}

export function validateRewardPerResponse(value: number | null): string | null {
  if (value == null || !Number.isFinite(value) || value <= 0) {
    return 'Please enter a reward amount'
  }
  if (value < MIN_REWARD_USD) {
    return minRewardPerResponseMessage()
  }
  return null
}

export function canConfirmRewardTab(rewardPerResponse: number | null): boolean {
  return validateRewardPerResponse(rewardPerResponse) === null
}

export function isTabClickable(
  tab: BudgetWizardTab,
  activeTab: BudgetWizardTab,
  confirmations: BudgetTabConfirmations,
  options?: { futureAudienceMode?: boolean; hasResponseTarget?: boolean },
): boolean {
  if (tab === activeTab) return true
  if (options?.futureAudienceMode && tab === 'campaign_budget' && !options.hasResponseTarget) {
    return false
  }
  /** After Tab 3 is visited, all three tabs stay navigable while prior tabs remain confirmed. */
  if (confirmations.campaignBudgetViewed) {
    return confirmations.responseTarget && confirmations.rewardPerResponse
  }
  if (tab === 'response_target') return true
  if (tab === 'reward_per_response') return confirmations.responseTarget
  if (tab === 'campaign_budget') {
    return confirmations.responseTarget && confirmations.rewardPerResponse
  }
  return false
}

export function hasCampaignBudgetCap(campaignBudgetAmount: number): boolean {
  return Number.isFinite(campaignBudgetAmount) && campaignBudgetAmount > 0
}

export function alreadyUsedOnOtherInstrumentsLabel(instrumentCount: number): string {
  const noun = instrumentCount === 1 ? 'instrument' : 'instruments'
  return `Already used on ${instrumentCount} other ${noun}`
}

/** Hide the sibling-instruments row when nothing is reserved on other instruments. */
export function shouldShowOtherInstrumentsRow(
  otherInstrumentCount: number,
  otherInstrumentsTotal: number,
): boolean {
  return otherInstrumentCount > 0 || otherInstrumentsTotal > 0
}

/** Hide the campaign pot when there is no cap and nothing reserved on sibling instruments. */
export function shouldShowCampaignBudgetCard(
  campaignBudgetAmount: number,
  otherInstrumentCount: number,
  otherInstrumentsTotal: number,
): boolean {
  if (hasCampaignBudgetCap(campaignBudgetAmount)) return true
  return shouldShowOtherInstrumentsRow(otherInstrumentCount, otherInstrumentsTotal)
}

export function computeCampaignBudgetRemaining(
  campaignBudgetAmount: number,
  otherInstrumentsTotal: number,
  thisInstrument: number,
): number {
  return campaignBudgetAmount - otherInstrumentsTotal - thisInstrument
}

/** Remaining-fill width for the campaign budget bar. Over-cap is a full red track. */
export function computeCampaignRemainingFill(
  remaining: number,
  campaignBudgetAmount: number,
): { fillPercent: number; isOver: boolean } {
  if (!hasCampaignBudgetCap(campaignBudgetAmount)) {
    return { fillPercent: 0, isOver: false }
  }
  if (remaining < 0) {
    return { fillPercent: 100, isOver: true }
  }
  return {
    fillPercent: Math.min(100, (remaining / campaignBudgetAmount) * 100),
    isOver: false,
  }
}

export function computeBudgetAllocationPercent(
  reservedToInstrument: number,
  campaignBudgetAmount: number,
): { allocatedPercent: number; remainingPercent: number } {
  if (campaignBudgetAmount <= 0) {
    return { allocatedPercent: 0, remainingPercent: 100 }
  }
  const allocatedPercent = Math.min(
    100,
    Math.round((reservedToInstrument / campaignBudgetAmount) * 100),
  )
  const remainingPercent = Math.max(0, 100 - allocatedPercent)
  return { allocatedPercent, remainingPercent }
}

/**
 * Wallet left after reserving this instrument.
 * `availableWalletBalance` is already net of sibling holds — do not subtract
 * other-LOI cost again.
 */
export function computeRemainingAfterInstrument(
  availableWalletBalance: number,
  reservedToInstrument: number,
): number {
  return Math.max(0, availableWalletBalance - reservedToInstrument)
}

export interface PreviewGateArgs {
  confirmations: BudgetTabConfirmations
  walletSufficient: boolean
  audienceMeetsMinimum: boolean
  budgetChecksReady: boolean
  rewardEnabled?: boolean
  responseTarget?: number | null
  rewardPerResponse?: number | null
  matchingSpCount?: number
  targetingMode?: ResponseTargetingModeOption
  futureAudienceMode?: boolean
  matchPending?: boolean
  minCollected?: number
}

/** Confirm on Reward is only for advancing to Campaign Budget after a target is set. */
export function shouldShowRewardConfirmAction(options: {
  futureAudienceMode: boolean
  hasResponseTarget: boolean
}): boolean {
  if (options.futureAudienceMode && !options.hasResponseTarget) return false
  return true
}

/** Visible Step 4 tabs — Response Target only when No Reward is selected. */
export function visibleBudgetWizardTabs(
  rewardEnabled: boolean,
  options?: { futureAudienceMode?: boolean },
): readonly BudgetWizardTab[] {
  if (options?.futureAudienceMode) {
    if (!rewardEnabled) return ['response_target'] as const
    return BUDGET_WIZARD_TABS
  }
  if (rewardEnabled) return BUDGET_WIZARD_TABS
  return ['response_target'] as const
}

function isFutureRewardReadyWithoutTarget(args: PreviewGateArgs): boolean {
  if (args.rewardPerResponse !== undefined) {
    return canConfirmRewardTab(args.rewardPerResponse)
  }
  return args.confirmations.rewardPerResponse
}

function isFuturePreviewEnabled(args: PreviewGateArgs): boolean {
  const targetingOptions = {
    targetingMode: args.targetingMode,
    optional: true,
    minCollected: args.minCollected,
  }
  if (
    validateResponseTarget(
      args.responseTarget,
      args.matchingSpCount ?? 0,
      targetingOptions,
    ) !== null
  ) {
    return false
  }
  if (args.rewardEnabled === false) return true
  if (!hasFixedResponseTarget(args.responseTarget)) {
    return isFutureRewardReadyWithoutTarget(args)
  }
  if (!args.confirmations.rewardPerResponse) return false
  if (!args.confirmations.campaignBudgetViewed) return false
  if (!args.budgetChecksReady) return false
  if (!args.walletSufficient) return false
  return true
}

/** Footer Preview — Reward: all tabs + wallet. No Reward: valid response target (no Confirm). */
export function isPreviewEnabled(args: PreviewGateArgs): boolean {
  if (args.futureAudienceMode) return isFuturePreviewEnabled(args)
  if (!args.audienceMeetsMinimum) return false
  const targetingOptions = {
    targetingMode: args.targetingMode,
    matchPending: args.matchPending,
    minCollected: args.minCollected,
  }
  if (args.rewardEnabled === false) {
    return canConfirmResponseTargetTab(
      args.responseTarget ?? 0,
      args.matchingSpCount ?? 0,
      targetingOptions,
    )
  }
  if (!args.confirmations.responseTarget) return false
  if (!args.confirmations.rewardPerResponse) return false
  if (!args.confirmations.campaignBudgetViewed) return false
  if (!args.budgetChecksReady) return false
  if (!args.walletSufficient) return false
  return true
}

export function shouldResetRewardConfirmation(
  prevTarget: number | null | undefined,
  nextTarget: number | null | undefined,
  rewardWasConfirmed: boolean,
): boolean {
  return rewardWasConfirmed && prevTarget !== nextTarget
}
